import { CreatedBy, Property, Realtor } from "@/types/property";
import { PropertyCurrencies } from "@/types/property-config";

// Define return types for strong autocompletion across the app
export interface GeneralInfo {
  id: string;
  reference: string;
  address: string;
  city: string;
  state: string;
  description: string;
  additional_info?: string;
  status: string;
  listing_type: string;
  property_category?: string;
  property_type: string;
  property_stage?: string;
  amenities?: string[];
  image_url: string[];
  realtor: Realtor;
  createdBy: CreatedBy | null;
  created_at: string;
}

export interface PricingDetails {
  price: number;
  currency?: PropertyCurrencies;
  is_negotiable?: boolean;
  total_package?: number;
  agency_fees?: number;
  legal_fees?: number;
  caution_fee?: number;
  service_fee?: number;
  // Lease & Shortlet specifics tied to financial commitment
  number_of_days?: number;
  lease_duration?: number;
  lease_duration_unit?: string;
}

export interface Stats {
  impressions_count: number;
  page_opened_count: number;
  inspection_count: number;
  save_count: number;
  whatsapp_contact_count: number;
  is_saved?: boolean;
  promote_to_weekly_deals: boolean;
  request_inspection: boolean;
}

export interface ResidentialFields {
  bedroom_count?: number;
  bathroom_count?: number;
  toilet?: number;
  serviced?: boolean;
  shared?: boolean;
  furnished?: boolean;
}

export interface CommercialFields {
  total_floor_area_sqm?: number;
  available_floor_space?: number;
  number_of_floors?: number;
  parking_capacity_cars?: number;
  is_multi_tenanted?: boolean;
  serviced?: boolean;
  shared?: boolean;
  furnished?: boolean;
}

export interface IndustrialFields {
  clear_ceiling_height_meters?: number;
  loading_docks_count?: number;
  power_capacity_kva?: number;
  total_land_area_sqm?: number;
  covered_area_sqm?: number;
  floor_load_capacity_tons?: number;
}

export interface LandFields {
  number_of_plots?: number;
  topography?: string;
  land_title_document?: string;
}

export interface SpecialPurposeFields {
  toilet?: number;
  total_floor_area_sqm?: number;
  parking_capacity_cars?: number;
}

export type CategoryBasedFields =
  | { category: "RESIDENTIAL"; data: ResidentialFields }
  | { category: "COMMERCIAL"; data: CommercialFields }
  | { category: "INDUSTRIAL"; data: IndustrialFields }
  | { category: "LAND"; data: LandFields }
  | { category: "SPECIAL_PURPOSE"; data: SpecialPurposeFields }
  | { category: "OTHER"; data: Record<string, unknown> };

export interface CategorizedProperty {
  general: GeneralInfo;
  pricingDetails: PricingDetails;
  stats: Stats;
  categoryBasedFields: CategoryBasedFields;
}

/**
 * Segregates a Property object into logical feature buckets matching domain schemas.
 */
export function categorizeProperty(property: Property): CategorizedProperty {
  const categoryUpper = (property.property_category || "").toUpperCase();

  // 1. General & Basic Info
  const general: GeneralInfo = {
    id: property.id,
    reference: property.reference,
    address: property.address,
    city: property.city,
    state: property.state,
    description: property.description,
    additional_info: property.additional_info,
    status: property.status,
    listing_type: property.listing_type,
    property_category: property.property_category,
    property_type: property.property_type,
    property_stage: property.property_stage,
    amenities: property.amenities,
    image_url: property.image_url,
    realtor: property.realtor,
    createdBy: property?.createdBy || null,
    created_at: property.created_at,
  };

  // 2. Pricing & Financials
  const pricingDetails: PricingDetails = {
    price: property.price,
    currency: property.currency,
    is_negotiable: property.is_negotiable,
    total_package: property.total_package,
    agency_fees: property.agency_fees,
    legal_fees: property.legal_fees,
    caution_fee: property.caution_fee,
    service_fee: property.service_fee,
    number_of_days: property.number_of_days,
    lease_duration: property.lease_duration,
    lease_duration_unit: property.lease_duration_unit,
  };

  // 3. User Analytics & Stats
  const stats: Stats = {
    impressions_count: property.impressions_count,
    page_opened_count: property.page_opened_count,
    inspection_count: property.inspection_count,
    save_count: property.save_count,
    whatsapp_contact_count: property.whatsapp_contact_count,
    is_saved: property.is_saved,
    promote_to_weekly_deals: property.promote_to_weekly_deals,
    request_inspection: property.request_inspection,
  };

  // 4. Category-Based Fields (Discriminated Union)
  let categoryBasedFields: CategoryBasedFields;

  switch (categoryUpper) {
    case "RESIDENTIAL":
      categoryBasedFields = {
        category: "RESIDENTIAL",
        data: {
          bedroom_count: property.bedroom_count,
          bathroom_count: property.bathroom_count,
          toilet: property.toilet,
          serviced: property.serviced,
          shared: property.shared,
          furnished: property.furnished,
        },
      };
      break;

    case "COMMERCIAL":
      categoryBasedFields = {
        category: "COMMERCIAL",
        data: {
          total_floor_area_sqm: property.total_floor_area_sqm,
          available_floor_space: property.available_floor_space,
          number_of_floors: property.number_of_floors,
          parking_capacity_cars: property.parking_capacity_cars,
          is_multi_tenanted: property.is_multi_tenanted,
          serviced: property.serviced,
          shared: property.shared,
          furnished: property.furnished,
        },
      };
      break;

    case "INDUSTRIAL":
      categoryBasedFields = {
        category: "INDUSTRIAL",
        data: {
          clear_ceiling_height_meters: property.clear_ceiling_height_meters,
          loading_docks_count: property.loading_docks_count,
          power_capacity_kva: property.power_capacity_kva,
          total_land_area_sqm: property.total_land_area_sqm,
          covered_area_sqm: property.covered_area_sqm,
          floor_load_capacity_tons: property.floor_load_capacity_tons,
        },
      };
      break;

    case "LAND":
      categoryBasedFields = {
        category: "LAND",
        data: {
          number_of_plots: property.number_of_plots,
          topography: property.topography,
          land_title_document: property.land_title_document,
        },
      };
      break;

    case "SPECIAL_PURPOSE":
    case "SPECIAL PURPOSE":
      categoryBasedFields = {
        category: "SPECIAL_PURPOSE",
        data: {
          toilet: property.toilet,
          total_floor_area_sqm: property.total_floor_area_sqm,
          parking_capacity_cars: property.parking_capacity_cars,
        },
      };
      break;

    default:
      categoryBasedFields = {
        category: "OTHER",
        data: {},
      };
      break;
  }

  return {
    general,
    pricingDetails,
    stats,
    categoryBasedFields,
  };
}
